import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState, type KeyboardEvent } from 'react';

import type { RegionNode } from '../api/regions';

import { rowStyle, type RegionTreeVariant } from './region-tree-styles';

interface RegionTreeNodeProps {
  node: RegionNode;
  depth: number;
  value: string;
  disabledIds: Set<string>;
  variant: RegionTreeVariant;
  onSelect: (id: string) => void;
}

function rowClasses(variant: RegionTreeVariant, disabled: boolean, selected: boolean): string {
  const base = 'flex items-center gap-1.5 px-2 py-1.5 rounded-md cursor-pointer select-none';
  if (disabled) return `${base} opacity-40 cursor-not-allowed`;
  if (variant === 'dashboards') return base;
  return selected
    ? `${base} bg-moss-100 dark:bg-moss-900/30 text-moss-700 dark:text-moss-400`
    : `${base} hover:bg-stone-100 dark:hover:bg-[#2a3530] text-bark-900 dark:text-stone-100`;
}

/** One row of the region tree, with its children when expanded. */
export default function RegionTreeNode({
  node,
  depth,
  value,
  disabledIds,
  variant,
  onSelect,
}: RegionTreeNodeProps) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children.length > 0;
  const disabled = disabledIds.has(node.id);
  const selected = value === node.id;

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
    e.preventDefault();
    if (!disabled) onSelect(node.id);
  };

  return (
    <div role="none">
      <div
        role="treeitem"
        tabIndex={-1}
        aria-label={node.name}
        aria-level={depth + 1}
        aria-selected={selected}
        aria-disabled={disabled || undefined}
        aria-expanded={hasChildren ? expanded : undefined}
        className={rowClasses(variant, disabled, selected)}
        style={rowStyle(variant, depth, selected)}
        onClick={() => {
          if (!disabled) onSelect(node.id);
        }}
        onKeyDown={onKeyDown}
      >
        {hasChildren ? (
          <button
            type="button"
            tabIndex={-1}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${node.name}`}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            className="p-0.5 rounded text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 flex-shrink-0"
          >
            {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
        ) : (
          <span className="w-4 flex-shrink-0" />
        )}
        <span className="text-sm">{node.name}</span>
        {disabled && <span className="ml-auto text-xs text-stone-400 italic">override set</span>}
      </div>

      {hasChildren && expanded && (
        <div role="group">
          {node.children.map((child) => (
            <RegionTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              value={value}
              disabledIds={disabledIds}
              variant={variant}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}
